/**
 * PURE selectors for academy analytics (F4-C): transform the server payload
 * into structures ready for the cards and mini-charts
 * (`DonutChart`/`BarChart`). No Solid or i18n dependencies → testable with
 * vitest. Readable LABELS (i18n) are set by the component.
 */
import type { AcademyMetrics, DebtMetrics, EnrollmentMetrics, RevenueMetrics } from "./api";
import { STAGE_ENROLLED } from "@/features/crm/api";

/** USD cents → USD (number with decimals). */
export const minorToUsd = (minor: number): number => minor / 100;

/** Canonical order of the aging buckets (mirror of Rust). */
export const AGING_BUCKET_ORDER = ["current", "1_30", "31_60", "61_90", "90_plus"] as const;
export type AgingBucketKey = (typeof AGING_BUCKET_ORDER)[number];

export interface MonthUsd {
  month: string;
  usdMinor: number;
}

/** Total revenue (USD cents) per month, aggregating method × currency. */
export function revenueByMonthTotals(revenue: RevenueMetrics): MonthUsd[] {
  const acc = new Map<string, number>();
  for (const p of revenue.byMonth) {
    acc.set(p.month, (acc.get(p.month) ?? 0) + p.usdMinor);
  }
  return [...acc.entries()]
    .map(([month, usdMinor]) => ({ month, usdMinor }))
    .toSorted((a, b) => a.month.localeCompare(b.month));
}

export interface MethodUsd {
  method: string;
  usdMinor: number;
}

/** Total revenue (USD cents) per payment method, descending. */
export function revenueByMethod(revenue: RevenueMetrics): MethodUsd[] {
  const acc = new Map<string, number>();
  for (const p of revenue.byMonth) {
    acc.set(p.method, (acc.get(p.method) ?? 0) + p.usdMinor);
  }
  return [...acc.entries()]
    .map(([method, usdMinor]) => ({ method, usdMinor }))
    .toSorted((a, b) => b.usdMinor - a.usdMinor);
}

/** Amount (USD cents) per original currency (for the dual USD/VES breakdown). */
export function revenueByCurrency(revenue: RevenueMetrics): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const p of revenue.byMonth) {
    acc[p.currency] = (acc[p.currency] ?? 0) + p.usdMinor;
  }
  return acc;
}

/** Aging buckets in canonical order, filling absent ones with 0. */
export function orderedAgingBuckets(debt: DebtMetrics): {
  bucket: AgingBucketKey;
  usdMinor: number;
  count: number;
}[] {
  const byKey = new Map(debt.buckets.map((b) => [b.bucket, b]));
  return AGING_BUCKET_ORDER.map((bucket) => {
    const found = byKey.get(bucket);
    return { bucket, usdMinor: found?.usdMinor ?? 0, count: found?.count ?? 0 };
  });
}

/** OVERDUE debt (everything that isn't `current`), in USD cents. */
export function overdueTotalMinor(debt: DebtMetrics): number {
  return debt.buckets.filter((b) => b.bucket !== "current").reduce((sum, b) => sum + b.usdMinor, 0);
}

/** Funnel's overall conversion rate (enrolled / total leads). 0 if none. */
export function funnelConversion(enrollment: EnrollmentMetrics): number {
  const total = enrollment.byStage.reduce((sum, s) => sum + s.count, 0);
  const enrolled = enrollment.byStage.find((s) => s.stage === STAGE_ENROLLED)?.count ?? 0;
  return total > 0 ? enrolled / total : 0;
}

/** Count of leads in a specific stage. */
export function stageCount(enrollment: EnrollmentMetrics, stage: string): number {
  return enrollment.byStage.find((s) => s.stage === stage)?.count ?? 0;
}

/** Bytes → human-readable text (KB/MB/GB), base 1024. */
const KIB = 1024;
export function formatBytes(bytes: number): string {
  if (bytes <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(KIB)));
  const value = bytes / KIB ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Total count of certificates issued in the monthly series. */
export function certificatesTotal(metrics: AcademyMetrics): number {
  return metrics.academic.certificatesByMonth.reduce((sum, m) => sum + m.count, 0);
}
